import { Component, OnInit, ViewEncapsulation, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Dome } from 'src/app/_models/dome';
import { DomesCreateComponent } from '../domes-create/domes-create.component';
import { Firestore, collection, doc, query, orderBy, setDoc, deleteDoc, updateDoc, DocumentReference, CollectionReference, getDocs } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Subject} from 'rxjs'; 
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Storage, ref, listAll, deleteObject } from '@angular/fire/storage';
import equal from 'fast-deep-equal';
import { environment, analyticsUrl } from 'src/environments/environment';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators'; 
import { Router, ActivatedRoute, ParamMap } from '@angular/router'; 
import { SharedService } from 'src/app/services/shared.service';
import { Config } from 'src/app/_models/config';
import { QRCodeElementType } from 'angularx-qrcode';
import { UrlBuilderService } from 'src/app/_services/url-builder.service';

@Component({
  selector: 'app-domes-list',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './domes-list.component.html',
  styleUrls: ['./domes-list.component.scss'],
  providers: [MessageService]
})
export class DomesListComponent implements OnInit, OnDestroy { // Implement OnDestroy

  @ViewChild("qrDisplay") qrDisplay: ElementRef;
  @ViewChild("qrBox") qrBox: ElementRef;
  @ViewChild("boundary") boundary: ElementRef;
  @ViewChild('canvas') canvas: ElementRef;
  @ViewChild('bgPlaceholder') bgPlaceholder: ElementRef;
  @ViewChild('bgImage') bgImage: ElementRef;
  @ViewChild('posterTextElement') posterTextElement: ElementRef;
  @ViewChild('textHolder') textHolder: ElementRef;

  version = environment.appVersion;
  public experienceOptions: MenuItem[]; // Renamed
  // public domes$: Observable<Dome[]>; // Replaced with direct array storage
  public domes: Dome[] = []; // Direct storage for experiences
  public allDomes: Dome[] = []; // Holds the full list of domes - Changed to public
  filteredDomes: Dome[] = []; // Holds the currently displayed domes
  searchTerm: string = ''; // For the search input
  isLoading: boolean = false; // Added loading state flag

  private _domesCollection: CollectionReference<Dome>; // Renamed for consistency, using Dome type
  private _dynamicDialogRef: DynamicDialogRef; // Keep this name as it's standard
  private clientId: string | null = null; // Added
  private projectId: string | null = null; // Added

  public user;
  public role: string;
  public selectedExperience: Dome; // Renamed, using Dome type

  NgxQrcodeElementTypes = NgxQrcodeElementTypes;
  elementType: QRCodeElementType = 'svg';
  correctionLevel = NgxQrcodeErrorCorrectionLevels.HIGH;
  qrUrl = '';
  qrUrlFallback = '';
  activeQRTab: number = 0;
  analyticsURL = "";
  logoURL: string | undefined; // Changed type from Observable<string> to string | undefined
  private FALLBACK_BASE_URL = 'https://chaparone.gallarie.co.uk/';

  JSON = JSON;

  private AR_PATH = "ar";
  private CLIENT_QUERY_PARAM = 'clt'
  private PROJECT_QUERY_PARAM = 'pro'
  private EXPERIENCE_QUERY_PARAM = 'exp'

  config: Config = new Config();
  environment;

  private unsubscriber: Subject<void> = new Subject<void>();

  showQRDialog: boolean = false;

  downloadOptions = [
    {
      label: 'Download SVG',
      icon: 'pi pi-download',
      command: () => {
        console.log('SVG download clicked');
        this.downloadQR('svg');
      }
    }
  ];

  constructor(public dialogService: DialogService,
    private firestore: Firestore, // Changed injection
    private confirmationService: ConfirmationService,
    private storage: Storage, // Changed injection
    public auth: Auth, // Changed injection
    private myanalytics: AnalyticsService,
    private router: Router,
    private route: ActivatedRoute, // Inject ActivatedRoute
    private sharedService: SharedService,
    private messageService: MessageService,
    private urlBuilder: UrlBuilderService) {
      this.environment = environment;
  }

  ngOnInit(): void {

    this.sharedService.role$.subscribe(role =>{
      this.role = role
    })
    // Subscribe to route parameters to get clientId and projectId
    this.route.paramMap.pipe(
      takeUntil(this.unsubscriber)
    ).subscribe(async (params: ParamMap) => { // Make async for getDocs
      this.clientId = params.get('clientId');
      this.projectId = params.get('projectId');

      if (this.clientId && this.projectId) {
        const path = `clients/${this.clientId}/projects/${this.projectId}/experiences`;
        this._domesCollection = collection(this.firestore, path) as CollectionReference<Dome>;
        console.log(`Setting up Firestore collection at path: ${path}`); // Debug log
        await this.loadExperiences(); // Initial load
      } else {
        console.error('Company ID or Project ID missing from route parameters.');
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load experiences: Missing route parameters.' });
        this.allDomes = []; // Clear domes if context is missing
        this.filterDomes();
      }
    });

    // Removed the direct subscription to domes$ as we now load manually


    history.pushState(null, '');

    fromEvent(window, 'popstate').pipe(
      takeUntil(this.unsubscriber)
    ).subscribe((event) => {
      const state = event as PopStateEvent;
      if (this._dynamicDialogRef) {
        //history.pushState(null, '');
        this._dynamicDialogRef.close();
      }
      else {
        history.back;
      }
    });
  }

  scale = 1.0;
  autoResizeQr: boolean = false;

  ngOnDestroy(): void {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }

  // Method to navigate back to projects view
  navigateBackToProjects(): void {
    this.router.navigate(['/projects']);
  }

  // Method to load experiences manually
  async loadExperiences(): Promise<void> {
    this.isLoading = true; // Set loading to true
    if (!this._domesCollection) {
      console.error("Experience collection reference not initialized.");
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load experiences: Collection not ready.' });
      return;
    }
    try {
      const domesQuery = query(this._domesCollection, orderBy('dateCreated', 'desc'));
      // Use getDocs for a single fetch instead of collectionData
      const querySnapshot = await getDocs(domesQuery);
      const snapshot = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Dome));
      console.log('Experiences loaded manually (length):', snapshot?.length, snapshot); // Debug log
      this.allDomes = snapshot ? [...snapshot] : []; // Store the full list
      this.filterDomes(); // Update filtered list
    } catch (err) {
      console.error('Error fetching experiences manually:', err);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load experiences.' });
      this.allDomes = []; // Clear on error
      this.filterDomes();
    } finally {
      this.isLoading = false; // Set loading to false regardless of success or error
    }
  }


  // Filter domes based on search term
  filterDomes(): void {
    if (!this.allDomes) {
      this.filteredDomes = [];
      return;
    }

    const searchTermLower = this.searchTerm.toLowerCase().trim();

    if (!searchTermLower) {
      this.filteredDomes = [...this.allDomes]; // Show all if search is empty
    } else {
      this.filteredDomes = this.allDomes.filter(dome =>
        dome.name.toLowerCase().includes(searchTermLower)
      );
    }
  }

  public onNewExperienceClicked() { // Renamed method
    if (!this.clientId || !this.projectId) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Cannot create experience: Missing context.' });
        return;
    }
    this.myanalytics.logEvent("button_click", { button: "NewExperienceClicked", user: this.user?.uid, company: this.clientId, project: this.projectId, name: "New Experience" });
    this._dynamicDialogRef = this.dialogService.open(DomesCreateComponent, { // Still uses DomesCreateComponent
      styleClass: 'p-col-12 p-md-8 p-xl-6 create-background experience-dialog',
      width: '100%',
      height: '100%',
      showHeader: false,
      closeOnEscape: true,
      data: {
        // Pass clientId and projectId if DomesCreateComponent needs them
        clientId: this.clientId,
        projectId: this.projectId,
        domes: this.allDomes, // Pass all experiences for navigation
        messageService: this.messageService
      }
    });

    this._dynamicDialogRef.onClose.pipe(takeUntil(this.unsubscriber)).subscribe(async (returnData: any) => {
      if (returnData) {
        let experience = returnData.dome as Dome; // Use Dome type

        this.createExperience(experience); // Call renamed method
        this.myanalytics.logEvent("button_click", {
          button: "ExperienceCreated", user: this.user?.uid, company: this.clientId, project: this.projectId, id: experience.id, features: JSON.stringify(experience.features), story: returnData.story,
          description: experience.description, name: experience.name
        });
      }
      this._dynamicDialogRef = undefined;
    });
  }

  public onUpdateExperienceClicked(experience: Dome) { // Renamed method and parameter (Dome type)
    if (!this.clientId || !this.projectId) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Cannot update experience: Missing context.' });
        return;
    }
    this.myanalytics.logEvent("button_click", { button: "UpdateExperienceClicked", user: this.user?.uid, company: this.clientId, project: this.projectId, id: experience.id, name: experience.name });
    let originalExperience = experience; // Renamed
    this._dynamicDialogRef = this.dialogService.open(DomesCreateComponent, { // Still uses DomesCreateComponent

      styleClass: 'p-col-12 p-md-8 p-xl-6 create-background experience-dialog',
      showHeader: false,
      width: '100%',
      height: '100%',
      closeOnEscape: true,
      data: {
        clientId: this.clientId, // Pass context
        projectId: this.projectId,
        dome: experience, // Pass experience data (as 'dome' if component expects that)
        domes: this.allDomes, // Pass all experiences for navigation
        messageService: this.messageService
      // domes: this.domes$
      },

    });

    this._dynamicDialogRef.onClose.pipe(takeUntil(this.unsubscriber)).subscribe(async (returnData: any) => {
      if (returnData) {
        let updatedExperience = returnData.dome as Dome; // Use Dome type

        if (!equal(originalExperience, updatedExperience)) {
          if (returnData.delete) {
            this.deleteFiles(updatedExperience, returnData.delete); // Pass updated experience
          }
          this.updateExperience(updatedExperience); // Call renamed method
        }
      }
      this._dynamicDialogRef = undefined;
    });
  }

  public toggleMenu(menu: Menu, experience: Dome, event: Event) { // Renamed parameter (Dome type)
    if (this.role === 'admin' || this.role === 'editor' || this.role === 'super')
      this.generateMenuOptions(experience, event.currentTarget); // Pass experience
    else
      this.generateViewerMenuOptions(experience, event.currentTarget); // Pass experience
    menu.toggle(event);
    setTimeout(() => {
      menu.alignOverlay();
    })
  }

  private async createExperience(experience: Dome): Promise<string | null> { // Renamed method and parameter (Dome type), make async

    console.log("calling create experience with " + this.JSON.stringify(experience))
     if (!this.clientId || !this.projectId || !this._domesCollection) {
         console.error("Cannot create experience: Missing context or collection reference.");
         this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create experience: context missing.' });
         return null;
      }
     // Use the existing _domesCollection reference

     try {
       // Create a plain object from the Dome instance but preserve the Timestamp
       const dateCreated = experience.dateCreated; // Save the Timestamp
       const plainObject = {...experience}; // Convert to plain object
       plainObject.dateCreated = dateCreated; // Restore the Timestamp
       
       // Use setDoc with the plain object that has the preserved Timestamp
       await setDoc(doc(this._domesCollection, experience.id), plainObject);
       console.log("Experience Created with ID:", experience.id); // Use experience.id
       this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Experience created successfully.' });
       await this.loadExperiences(); // Refresh list after creation
       return experience.id; // Return the pre-set ID
     } catch (reason) {
       console.error("Error creating experience:", reason);
       this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create experience.' });
       return null;
     }
  }

  private async removeExperience(experience: Dome) { // Renamed method and parameter (Dome type), make async
    if (!this.clientId || !this.projectId) {
        console.error("Cannot delete experience: Missing context.");
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete experience: context missing.' });
        return;
    }
    this.myanalytics.logEvent("button_click", { button: "DeleteClicked", user: this.user?.uid, company: this.clientId, project: this.projectId, id: experience.id });

    const experienceStorageBasePath = `clients/${this.clientId}/projects/${this.projectId}/experiences/${experience.id}`;
    const experienceStorageRef = ref(this.storage, experienceStorageBasePath);
    const experienceDocRef = doc(this.firestore, `clients/${this.clientId}/projects/${this.projectId}/experiences/${experience.id}`);

    // Delete storage contents first
    listAll(experienceStorageRef).then(async listResult => {
      const deletePromises = listResult.items.map(itemRef => deleteObject(itemRef));
      await Promise.all(deletePromises);
      console.log(`Storage for experience ${experience.id} cleared.`);
      // Then delete Firestore document
      await deleteDoc(experienceDocRef);
      console.log(`Firestore doc for experience ${experience.id} deleted.`);
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Experience deleted successfully.' });
      await this.loadExperiences(); // Refresh list after deletion
    }).catch(error => {
      console.error(`Error deleting experience ${experience.id} or its files:`, error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete experience.' });
      // Optionally, try to delete the Firestore doc even if storage deletion fails
      deleteDoc(experienceDocRef).catch(docError => console.error("Error deleting Firestore doc after storage error:", docError));
    });
  }

  private async updateExperience(experience: Dome): Promise<void> { // Renamed method and parameter (Dome type), make async
    if (!this.clientId || !this.projectId) {
        console.error("Cannot update experience: Missing context.");
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update experience: context missing.' });
        return;
    }
    const experienceDocRef = doc(this.firestore, `clients/${this.clientId}/projects/${this.projectId}/experiences/${experience.id}`) as DocumentReference<Dome>; // Use Dome type
    
    // Create a plain object from the Dome instance but preserve any Timestamp objects
    const dateUpdated = experience.dateUpdated; // Save the Timestamp if it exists
    const dateCreated = experience.dateCreated; // Save the original creation timestamp if it exists
    
    // Create a plain object with the fields we want to update
    const plainObject = {
        features: experience.features,
        published: experience.published,
        name: experience.name,
        geo: experience.geo
    };
    
    // Add the timestamps back if they exist
    if (dateUpdated) {
        plainObject['dateUpdated'] = dateUpdated;
    }
    if (dateCreated) {
        plainObject['dateCreated'] = dateCreated;
    }
    
    updateDoc(experienceDocRef, plainObject).then(async () => { // Make the .then callback async
        console.log(`Experience ${experience.id} updated.`);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Experience updated successfully.' });
        await this.loadExperiences(); // Refresh list after update
    }).catch(error => {
        console.error(`Error updating experience ${experience.id}:`, error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update experience.' });
    });
  }

  private async publishExperience(experience: Dome): Promise<void> { // Renamed method and parameter (Dome type), make async
    if (!this.clientId || !this.projectId) {
        console.error("Cannot publish experience: Missing context.");
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to publish experience: context missing.' });
        return;
    }
    experience.published = !experience.published;
    const experienceDocRef = doc(this.firestore, `clients/${this.clientId}/projects/${this.projectId}/experiences/${experience.id}`);
    updateDoc(experienceDocRef, { published: experience.published }).then(async () => { // Make the .then callback async
        console.log(`Experience ${experience.id} publish status set to ${experience.published}.`);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `Experience ${experience.published ? 'published' : 'unpublished'}.` });
        await this.loadExperiences(); // Refresh list after publish status change
    }).catch(error => {
        console.error(`Error updating publish status for experience ${experience.id}:`, error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update publish status.' });
    });
    this.myanalytics.logEvent("button_click", { button: "publishClicked", published: experience.published, id: experience.id, user: this.user?.uid, company: this.clientId, project: this.projectId });
  }

  decodeHtml(html) {
    var txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }

  private deleteFiles(experience: Dome, files: string[]) { // Renamed parameter (Dome type)
    if (!this.clientId || !this.projectId) {
        console.error("Cannot delete files: Missing context.");
        return;
    }
    const experienceStorageBasePath = `clients/${this.clientId}/projects/${this.projectId}/experiences/${experience.id}`;
    files.forEach(async file => {
      const filePath = `${experienceStorageBasePath}/${file}`;
      console.log("Attempting to delete file: " + filePath);
      const fileRef = ref(this.storage, filePath);
      try {
        await deleteObject(fileRef);
        console.log(`Successfully deleted file: ${filePath}`);
      } catch (error) {
        // Handle errors, e.g., file not found
        if (error.code !== 'storage/object-not-found') {
          console.error(`Failed to delete file ${filePath}:`, error);
        } else {
          console.log(`File not found, skipping deletion: ${filePath}`);
        }
      }
    });
  }

  private confirmDomeDelete(message: string, accept: () => any) { // Removed target parameter
    this.confirmationService.confirm({
      // Removed target property implicitly by not passing it
      message: message, // Use the dynamic message passed in
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        console.log('Confirmation accepted! Running callback...'); // Added log
        accept();
      },
    });
  }

  public togglePublish(experience: Dome, event: Event) { // Renamed parameter (Dome type)
    this.confirmDomeDelete(`Are you sure that you want to ${experience.published ? 'unpublish' : 'publish'} ${experience.name}?`, () => { this.publishExperience(experience); }); // Removed event.currentTarget
  }

  public openGoogleMap(experience: Dome) { // Renamed parameter (Dome type)
    if (!experience.geo) return; // Add check for geo property
    window.open(`http://www.google.com/maps/place/${experience.geo.latitude},${experience.geo.longitude}`, '_blank').focus();
  }

  public openAnalytics(experience: Dome, event: Event) { // Renamed parameter (Dome type)
    // TODO: Verify if analytics URL structure needs company/project IDs
    const url = analyticsUrl(this.config.analyticsId, experience.id);
    window.open(url, '_blank').focus();
  }

  public deleteExperience(experience: Dome, event: Event) { // Renamed method and parameter (Dome type)
    this.confirmDomeDelete(`Are you sure that you want to delete ${experience.name}?`, () => { this.removeExperience(experience); }); // Removed event.currentTarget
  }

  public openExperienceViewer(experience: Dome) { // Renamed method and parameter (Dome type)
    console.log(experience.name);
    this.myanalytics.logEvent("button_click", { button: "OpenExperienceClicked", user: this.user?.uid, company: this.clientId, project: this.projectId, id: experience.id, name: experience.name });
    // TODO: Verify if the base URL needs company/project context or if the experience ID is globally unique
    const queryParams = {};
    queryParams[this.CLIENT_QUERY_PARAM] = this.clientId;
    queryParams[this.PROJECT_QUERY_PARAM] = this.projectId;
    queryParams[this.EXPERIENCE_QUERY_PARAM] = experience.id;

    const url = this.urlBuilder.build(environment.baseUrl, {
      queryParams: queryParams
    });

    window.open(url, '_blank').focus();
  }

  public showQR(experience: Dome) { // Renamed parameter (Dome type)
    this.selectedExperience = experience; // Use renamed property
    // TODO: Verify if the QR URL needs company/project context
    const queryParams = {};
    queryParams[this.CLIENT_QUERY_PARAM] = this.clientId;
    queryParams[this.PROJECT_QUERY_PARAM] = this.projectId;
    queryParams[this.EXPERIENCE_QUERY_PARAM] = experience.id;

    // Generate main URL using environment baseURL
    this.qrUrl = this.urlBuilder.build(environment.baseUrl, {
      queryParams: queryParams
    });

    // Generate fallback URL for old experiences
    this.qrUrlFallback = this.urlBuilder.build(this.FALLBACK_BASE_URL, {
      queryParams: queryParams
    });

    this.activeQRTab = 0; // Reset to main tab
    this.showQRDialog = true;
    this.myanalytics.logEvent("button_click", { button: "QRCode", user: this.user?.uid, company: this.clientId, project: this.projectId, id: experience.id });
  }

  public hideQR() {
    this.showQRDialog = false;
    this.selectedExperience = null; // Use renamed property
    this.qrUrl = '';
    this.qrUrlFallback = '';
    this.activeQRTab = 0;
  }

  public downloadQR(format: 'png' | 'svg') {
    // Get all qrcode elements and select the one from the active tab
    const qrElements = document.querySelectorAll('qrcode');
    if (!qrElements || qrElements.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'QR Code not found'
      });
      return;
    }

    // Select the QR code from the active tab (0 = main, 1 = fallback)
    const activeTabIndex = this.activeQRTab;
    const qrElement = qrElements[activeTabIndex] as HTMLElement;
    
    if (!qrElement) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'QR Code not found for active tab'
      });
      return;
    }

    const urlType = this.activeQRTab === 0 ? 'main' : 'fallback';

    if (format === 'svg') {
      // Get SVG element and clone it
      const svgElement = qrElement.querySelector('svg');
      if (!svgElement) return;
      
      const svgString = new XMLSerializer().serializeToString(svgElement);
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-code-${urlType}-${Date.now()}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } else {
      // For PNG, render SVG to canvas at high resolution
      const svgElement = qrElement.querySelector('svg');
      if (!svgElement) return;

      const canvas = document.createElement('canvas');
      const scale = 10; // Higher scale for better quality
      canvas.width = svgElement.viewBox.baseVal.width * scale;
      canvas.height = svgElement.viewBox.baseVal.height * scale;
      
      const ctx = canvas.getContext('2d');
      const svgString = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();
      
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `qr-code-${urlType}-${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 'image/png', 1.0);
      };
      
      img.src = 'data:image/svg+xml;base64,' + btoa(svgString);
    }
  }

  public generateMenuOptions(experience: Dome, event: any) { // Made public and renamed parameter
    this.experienceOptions = [{ // Renamed property
      label: 'View',
      items: [
        {
          label: 'QR Code',
          icon: 'pi pi-qrcode',
          command: () => {
            this.showQR(experience);
          }
        },
        {
          label: 'View Map',
          icon: 'pi pi-map',
          disabled: (!experience.geo || (experience.geo.latitude === 0 && experience.geo.longitude === 0)), // Disable if no geo data
          command: (event: any) => {
            if (experience.geo) {
              this.openGoogleMap(experience); // Call renamed method
            }
          }
        }
      ]
    },
    {
      label: 'Mange',
      items: [
        {
          label: 'Edit',
          icon: 'pi pi-pencil',
          command: () => {
            this.onUpdateExperienceClicked(experience); // Call renamed method
          }
        },
        {
          label: experience.published ? 'Unpublish' : 'Publish',
          icon: experience.published ? 'pi pi-eye-slash' : 'pi pi-eye',
          command: (event: any) => {
            this.togglePublish(experience, event.originalEvent);
          }
        },
        {
          label: 'Delete',
          icon: 'pi pi-trash',
          command: (event: any) => {
            console.log('Calling delete experience');
            this.deleteExperience(experience, event.originalEvent);
          }
        }
      ]
    }
    ];
  }

  public generateViewerMenuOptions(experience: Dome, event: any) { // Made public and renamed parameter
    this.experienceOptions = [{ // Renamed property
      label: 'View',
      items: [
        {
          label: 'QR Code',
          icon: 'pi pi-qrcode',
          command: () => {
            this.showQR(experience);
          }
        },
        {
          label: 'View Details',
          icon: 'pi pi-info-circle',
          command: () => {
            this.onUpdateExperienceClicked(experience); // Call renamed method (opens dialog in view mode for 'user' role)
          }
        },
        {
          label: 'View Map',
          icon: 'pi pi-map',
          disabled: (!experience.geo || (experience.geo.latitude === 0 && experience.geo.longitude === 0)),
          command: (event: any) => {
             if (experience.geo) {
               this.openGoogleMap(experience); // Call renamed method
             }
          }
        }
      ]
    }
    ];
  }


  public openCompetitions() {
    this.myanalytics.logEvent("button_click", { button: "Competitions", user: this.user.id });
    this.router.navigate(['competitions']);
  }

  public openPdf() {
    window.open('assets/pdf/Gallarie_Content_Upload_Agreement.pdf')
  }

  async getBase64ImageFromUrl(imageUrl) {
    var res = await fetch(imageUrl);
    var blob = await res.blob();

    return new Promise((resolve, reject) => {
      var reader = new FileReader();
      reader.addEventListener("load", function () {
        resolve(reader.result);
      }, false);

      reader.onerror = () => {
        return reject(this);
      };
      reader.readAsDataURL(blob);
    })
  }

  imageLoaded(bgImage) {
    setTimeout(() => {
      console.log("Set height to: " + bgImage.nativeElement.width);
      bgImage.nativeElement.style.height = bgImage.nativeElement.width + 'px';
    }, 0.5);
  }

  showAnalytics() {
    this.myanalytics.logEvent("button_click", { button: "GlobalAnalytics", user: this.user.id });
    let url = environment.combinedAnalytics;
    window.open(url, '_blank').focus();
  }

  public manageUser() {
    this.myanalytics.logEvent("button_click", { button: "ManageAccount", user: this.user.id });
    this.router.navigate(['user-manage']);
  }


  saveConfig() {
    const configDocRef = doc(this.firestore, "config/Config"); // Create doc ref
    
    // Create a plain object but preserve any potential Timestamp objects
    // Note: This is a generic approach that will work even if Config doesn't have Timestamps
    const plainObject = {...this.config};
    
    // If you know specific Timestamp fields in Config, you could handle them explicitly:
    // const someTimestamp = this.config.someTimestamp;
    // const plainObject = {...this.config};
    // plainObject.someTimestamp = someTimestamp;
    
    setDoc(configDocRef, plainObject).then(() => { // Use setDoc
      console.log("Config saved");
    });
  }

  public copyUrl(url: string) {
    navigator.clipboard.writeText(url).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'URL copied to clipboard'
      });
    }).catch(() => {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to copy URL'
      });
    });
  }

  public openUrl(url: string) {
    window.open(url, '_blank');
  }
}


